import { useState } from "react";
import { useT } from "../../i18n/hooks.ts";
import type { TextKey } from "../../i18n/es.ts";
import { emptyProject } from "../../model/projectEntities.ts";
import type { ProjectSeries } from "../../model/dailySeries.ts";
import type { SubBasinLayer } from "../../model/gisLayer.ts";
import type { LoadedProject } from "../../model/project.ts";
import type { EvalHidProject } from "../../model/types.ts";
import type { MitecoOrigin } from "../../model/storage.ts";
import type { AemetQueuedRequest } from "../../model/aemet/aemetJobStore.ts";
import { validateProject } from "../../model/validations.ts";
import { Step1SubBasins, type MitecoFinishPayload } from "./Step1SubBasins.tsx";
import { Step2Zones } from "../Step2Zones.tsx";
import { Step3Outlets } from "./Step3Outlets.tsx";
import { Step4Topology } from "./Step4Topology.tsx";
import { Step5Series } from "./Step5Series.tsx";

const STEP_TITLES: TextKey[] = [
  "panels.subBasins",
  "panels.zones",
  "newProject.outletPoints",
  "infoPopover.aquifers",
  "common.series",
  "newProject.review",
];
const LAST_STEP = STEP_TITLES.length;

/**
 * Wizard to create an EvalHid project from scratch (see PLAN_VIABILIDAD.md,
 * point 10). Step 0 asks for the project's name before anything else — it's
 * the one thing every later "Finish now" needs — and explains that the
 * wizard doesn't have to be walked to the end: every step from here on has
 * both "Next" (keep configuring) and "Finish" (create the project with
 * whatever's set up so far; the rest — more sub-basins, zones, series... —
 * is just as easy to add later from "Edit project" on the created one).
 * Only the last step (Review) drops "Next", since there's nowhere further
 * to go.
 *
 * `project` is a real (possibly empty) `EvalHidProject` from the start,
 * not `null` until step 1 finishes — this is what lets "Finish" work from
 * ANY step, including step 0 itself (equivalent to Step1SubBasins' own
 * "Start with no sub-basins", just reachable one step earlier).
 */
export function NewProject({
  onComplete,
  onCancel,
}: {
  onComplete: (
    loadedProject: LoadedProject,
    gisLayer: SubBasinLayer | null,
    name: string,
    /** Only set when "Finalizar" fires via `mitecoFinishAction` (MitecoStep, Step1SubBasins.tsx) — see MitecoFinishPayload's own comment for why that path needs extra bookkeeping the normal wizard finish doesn't. */
    miteco?: { mitecoOrigins: Record<number, MitecoOrigin>; aemetRequests: AemetQueuedRequest[] }
  ) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [step, setStep] = useState(0);
  const [project, setProject] = useState<EvalHidProject>(emptyProject());
  const [gisLayer, setGisLayer] = useState<SubBasinLayer | null>(null);
  const [name, setName] = useState("");
  /** Set by Step1SubBasins.tsx while its own MITECO tab is active — see its `onMitecoTabActive` comment. */
  const [mitecoTabActive, setMitecoTabActive] = useState(false);
  /** MitecoStep's own finish, once a basin with a non-empty result is picked — `null` otherwise. Feeds the nav "Finalizar" button below instead of a second, duplicate button inside the tab (see Step1SubBasins.tsx's `onMitecoFinishReady`). */
  const [mitecoFinishAction, setMitecoFinishAction] = useState<(() => void) | null>(null);
  const [series, setSeries] = useState<ProjectSeries>({
    precipitation: null,
    etp: null,
    temperature: null,
    pumping: null,
  });

  const canProceed = name.trim() !== "";

  function finish() {
    onComplete({ project, series, notices: [] }, gisLayer, name.trim());
  }

  function finishFromMiteco(payload: MitecoFinishPayload) {
    onComplete({ project: payload.project, series, notices: [] }, payload.layer, name.trim(), {
      mitecoOrigins: payload.mitecoOrigins,
      aemetRequests: payload.aemetRequests,
    });
  }

  return (
    <div className="new-project">
      <div className="new-project-card">
        <div className="new-project-header">
          <h2>{t("newProject.title")}</h2>
          <button className="button-icon" onClick={onCancel} aria-label={t("common.cancel")}>
            ✕
          </button>
        </div>

        {step > 0 && (
          <ol className="new-project-steps">
            {STEP_TITLES.map((key, i) => (
              <li key={key} className={i + 1 === step ? "current-step" : undefined}>
                {i + 1}. {t(key)}
              </li>
            ))}
          </ol>
        )}

        <div className="new-project-body">
          {step === 0 && (
            <div>
              <h3>{t("newProject.projectName")}</h3>
              <div className="step-form">
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("newProject.projectName")}
                  onKeyDown={(e) => e.key === "Enter" && canProceed && setStep(1)}
                />
              </div>
              <p className="muted-text">{t("newProject.wizardExplanation")}</p>
            </div>
          )}
          {step === 1 && (
            <Step1SubBasins
              project={project}
              onChangeProject={setProject}
              onComplete={(p, layer) => {
                setProject(p);
                setGisLayer(layer);
                setStep(2);
              }}
              onFinishNow={finishFromMiteco}
              onMitecoTabActive={setMitecoTabActive}
              onMitecoFinishReady={(fn) => setMitecoFinishAction(() => fn)}
            />
          )}
          {step === 2 && <Step2Zones project={project} gisLayer={gisLayer} onChange={setProject} />}
          {step === 3 && (
            <Step3Outlets project={project} gisLayer={gisLayer} onChange={setProject} />
          )}
          {step === 4 && (
            <Step4Topology project={project} gisLayer={gisLayer} onChange={setProject} />
          )}
          {step === 5 && (
            <Step5Series
              project={project}
              onChangeProject={setProject}
              series={series}
              onChange={setSeries}
            />
          )}
          {step === LAST_STEP &&
            (() => {
              const validation = validateProject(t, project, series);
              return (
                <div>
                  <h3>
                    {LAST_STEP}. {t("newProject.review")}
                  </h3>
                  <dl className="data-summary">
                    <dt>{t("panels.subBasins")}</dt>
                    <dd>{project.subBasins.length}</dd>
                    <dt>{t("panels.zones")}</dt>
                    <dd>{project.zones.length}</dd>
                    <dt>{t("infoPopover.outletPoints")}</dt>
                    <dd>{project.outletPoints.length}</dd>
                    <dt>{t("infoPopover.aquifers")}</dt>
                    <dd>{project.aquifers.length}</dd>
                  </dl>
                  {validation.blocking.length > 0 && (
                    <details className="collapsible-section">
                      <summary>
                        ⛔{" "}
                        {t(
                          validation.blocking.length === 1
                            ? "newProject.errorCount"
                            : "newProject.errorsCount",
                          { n: validation.blocking.length }
                        )}
                      </summary>
                      {validation.blocking.map((a) => (
                        <p key={a} className="engine-notice engine-notice-blocking">
                          {a}
                        </p>
                      ))}
                    </details>
                  )}
                  {validation.notices.length > 0 && (
                    <details className="collapsible-section">
                      <summary>
                        ⚠{" "}
                        {t(
                          validation.notices.length === 1
                            ? "newProject.warningCount"
                            : "newProject.warningsCount",
                          { n: validation.notices.length }
                        )}
                      </summary>
                      {validation.notices.map((a) => (
                        <p key={a} className="engine-notice">
                          {a}
                        </p>
                      ))}
                    </details>
                  )}
                </div>
              );
            })()}
        </div>

        <div className="new-project-nav">
          {step > 0 ? (
            <button className="button" onClick={() => setStep((p) => p - 1)}>
              {t("common.back")}
            </button>
          ) : (
            <span />
          )}
          <div className="new-project-nav-forward">
            <button
              className={step === LAST_STEP || mitecoTabActive ? "button button-primary" : "button"}
              onClick={mitecoTabActive && mitecoFinishAction ? mitecoFinishAction : finish}
              disabled={!canProceed || (mitecoTabActive && !mitecoFinishAction)}
            >
              {t("common.finish")}
            </button>
            {step < LAST_STEP && !mitecoTabActive && (
              <button
                className="button button-primary"
                onClick={() => setStep((p) => p + 1)}
                disabled={!canProceed}
              >
                {t("common.next")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
